import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Trophy,
  CheckCircle,
  Shield,
  Zap,
  User,
  Clock,
  Award,
  Send,
} from "lucide-react";
import pic1 from "../../../assets/images/web development 1.jpg";
import pic2 from "../../../assets/images/web development.jpg";

const WebDevelopment = () => {
  return (
    <div className="bg-white-100 text-gray-800" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  🌐 Web Development Services by Right On{" "}
                </span>
                <span className="text-blue-600">Time Services</span>
              </h1>
              <p className="mt-4 mb-4 font-bold">
                Smart, Scalable, and Designed for Success
              </p>
              <p className="text-white mb-8">
                At Right On Time Services, we blend creativity with cutting-edge
                technology to craft websites that engage, convert, and grow your
                business. From intuitive UI/UX to robust backend systems, our
                end-to-end web development solutions are secure, scalable, and
                tailored to your needs.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <section
        className="py-20 bg-white px-4 md:px-10 mt-4 mb-4"
        style={{ padding: "26px" }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-blue-700 mb-6">
            🧠 Your Vision, Our Expertise
          </h2>
          <p className="text-gray-700 text-md mb-4">
            Whether you're starting fresh or reimagining your current site, we
            offer full-spectrum web development services that deliver results.
            Our agile, cross-functional team includes:
          </p>
          <ul className="text-gray-700 list-inside text-center max-w-xl mx-auto mb-6">
            <li>Business Analysts</li>
            <li>Product Designers</li>
            <li>Project Managers</li>
            <li>QA Engineers</li>
            <li>Full-Stack Developers</li>
          </ul>
          <p className="text-gray-700 text-md">
            Together, we bring your digital vision to life with precision and
            purpose.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-100 px-6 md:px-16">
        <div className="container mx-auto grid md:grid-cols-2 gap-8 items-center">
          {/* Left Content */}
          <div>
            <h2 className="text-3xl font-bold text-blue-900 text-left mb-6">
              🛠️ Our Web Development Services
            </h2>

            <div className="grid gap-6">
              <motion.div
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-xl font-bold text-blue-900">
                  🎨 UI/UX Design & Development
                </h3>
                <p className="mt-2">
                  We craft custom user interfaces that are both visually
                  stunning and intuitively functional, ensuring a seamless user
                  experience that reflects your brand identity.
                </p>
              </motion.div>

              <motion.div
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-xl font-bold text-blue-900">
                  🛍️ E-Commerce Development
                </h3>
                <p className="mt-2">
                  Boost your sales with secure, streamlined shopping
                  experiences. We integrate payment gateways, design product
                  catalogs, and optimize checkout flows for maximum conversion.
                </p>
              </motion.div>

              <motion.div
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-xl font-bold text-blue-900">
                  🗂️ Content Management Systems (CMS)
                </h3>
                <p className="mt-2">
                  Manage your content with ease. From blogs to enterprise-level
                  sites, we build custom CMS solutions that put you in
                  control—without the tech headaches.
                </p>
              </motion.div>

              <motion.div
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-xl font-bold text-blue-900">
                  🧩 Custom Website Development
                </h3>
                <p className="mt-2">
                  No templates here—we build websites from the ground up. Our
                  responsive, user-friendly solutions are tailored to your
                  business goals and designed to scale.
                </p>
              </motion.div>

              <motion.div
                className="p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition duration-300"
                whileHover={{ scale: 1.05 }}
              >
                <h3 className="text-xl font-bold text-blue-900">
                  💻 WordPress Website Solutions
                </h3>
                <p className="mt-2">
                  Need flexibility? We specialize in custom WordPress
                  development, whether you’re starting from scratch or upgrading
                  an existing site. We offer:
                </p>
                <ul className="list-disc list-inside mt-2 text-gray-700">
                  <li>Fully Customized WordPress Builds</li>
                  <li>Template Tweaks & Enhancements</li>
                  <li>Plugin Integration & Optimization</li>
                </ul>
              </motion.div>
            </div>
          </div>

          {/* Right Image */}
          <div className="flex justify-center">
            <img
              src={pic1}
              alt="Web Development"
              className="w-full max-w-sm md:max-w-md lg:max-w-lg h-auto object-contain rounded-xl"
            />
          </div>
        </div>
      </section>

      <motion.section
        className="rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <section
          className="py-20 bg-white px-4 md:px-10 mt-4 mb-4"
          style={{ padding: "30px" }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-blue-700 mb-6">
              🚀 Our Web Development Process
            </h2>
            <p className="text-gray-700 text-md mb-10">
              A streamlined, step-by-step journey to digital excellence:
            </p>
            <div className="grid gap-6 text-left max-w-3xl mx-auto">
              <div className="bg-gray-50 shadow-md rounded-xl p-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  1️⃣ Discovery
                </h3>
                <p className="text-gray-700">
                  We dive deep to understand your business, goals, target
                  audience, and competitors.
                </p>
              </div>
              <div className="bg-gray-50 shadow-md rounded-xl p-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  2️⃣ Planning
                </h3>
                <p className="text-gray-700">
                  We map out a detailed roadmap, outlining technology choices,
                  timelines, and functionality.
                </p>
              </div>
              <div className="bg-gray-50 shadow-md rounded-xl p-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  3️⃣ Design
                </h3>
                <p className="text-gray-700">
                  You’ll receive mockups and interactive prototypes to visualize
                  the final product.
                </p>
              </div>
              <div className="bg-gray-50 shadow-md rounded-xl p-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  4️⃣ Development
                </h3>
                <p className="text-gray-700">
                  Using the latest frameworks and coding standards, we build a
                  fast, secure, and scalable website.
                </p>
              </div>
              <div className="bg-gray-50 shadow-md rounded-xl p-6">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">
                  5️⃣ Testing & Launch
                </h3>
                <p className="text-gray-700">
                  We conduct comprehensive QA testing before launch and offer
                  post-launch support to keep your site running smoothly.
                </p>
              </div>
            </div>
          </div>
        </section>
      </motion.section>

      <motion.section
        className="rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <section
          className="py-20 bg-gray-50 px-4 md:px-10 mt-4 mb-4"
          style={{ padding: "30px" }}
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-700 text-center mb-10">
              🖼️ Explore Our Work
            </h2>
            <p className="text-center text-gray-700 mb-8">
              We take pride in what we build. Check out some of our standout
              projects:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 text-center">
              <li className="bg-white p-6 rounded-xl shadow-md">
                Extended Health Services
              </li>
              <li className="bg-white p-6 rounded-xl shadow-md">
                Scribe Align LLC
              </li>
              <li className="bg-white p-6 rounded-xl shadow-md">
                Clinical Heroes
              </li>
            </ul>
          </div>
        </section>
      </motion.section>

      <motion.section
        className="rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <section className="py-20 bg-white px-4 md:px-10">
          <div className="container mx-auto grid md:grid-cols-2 gap-8 items-center">
            {/* Left Image */}
            <div className="flex justify-center">
              <img
                src={pic2} // ← Replace with your actual image path
                alt="Trusted Services"
                className="w-full max-w-md h-auto object-contain rounded-"
              />
            </div>

            {/* Right Content */}
            <div>
              <h2 className="text-3xl font-bold text-blue-700 mb-6 text-left">
                🌟 Why Clients Trust Right On Time Services
              </h2>
              <div className="grid gap-6 text-left">
                <div className="bg-gray-100 shadow-md rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">
                    👨‍💻 Experienced & Skilled Team
                  </h3>
                  <p className="text-gray-700">
                    We’re fluent in the latest web technologies and best
                    practices—HTML5, CSS3, React, Node.js, PHP, WordPress, and
                    more.
                  </p>
                </div>
                <div className="bg-gray-100 shadow-md rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">
                    🔧 Tailored Solutions
                  </h3>
                  <p className="text-gray-700">
                    We don’t do cookie-cutter websites. Each build is aligned
                    with your brand, audience, and business strategy.
                  </p>
                </div>
                <div className="bg-gray-100 shadow-md rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">
                    📋 Proven Process
                  </h3>
                  <p className="text-gray-700">
                    Our organized workflow ensures every project is delivered
                    with quality, speed, and clarity.
                  </p>
                </div>
                <div className="bg-gray-100 shadow-md rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">
                    🤝 Continued Partnership
                  </h3>
                  <p className="text-gray-700">
                    We don’t stop at launch. We provide ongoing support,
                    updates, and maintenance to help your site evolve as your
                    business grows.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </motion.section>

      {/* Call to Action */}
      <motion.section
        className="py-16 text-center bg-white shadow-md rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <h2 className="text-3xl font-bold text-blue-900">
          💬 Let's Build Something Great Together
        </h2>
        <p className="mt-4 px-8 max-w-4xl mx-auto">
          Choosing Right On Time Services means choosing a committed partner.
          Let us help you create a powerful online presence that drives real
          results.
        </p>
        <p className="text-lg font-semibold text-blue-800">
          📞 Contact us today and let’s turn your vision into a high-performing
          website.
        </p>
      </motion.section>
    </div>
  );
};

export default WebDevelopment;
